import React, { useEffect, useRef, useState } from 'react';
import { Ad, Language } from '../types';

declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
    }
}

interface PageCTAProps {
    cta: Ad;
    language: Language;
    variant?: 'hero' | 'slim' | 'in-feed' | 'compact';
    className?: string;
}

const PageCTA: React.FC<PageCTAProps> = ({ cta, language, variant = 'hero', className = '' }) => {
    const headline = language === 'sv' ? (cta.headlineSv || cta.headline) : cta.headline;
    const description = language === 'sv' ? (cta.descriptionSv || cta.description) : cta.description;
    const ctaText = language === 'sv' ? (cta.callToActionSv || cta.callToAction) : cta.callToAction;

    const ref = useRef<HTMLDivElement>(null);
    const [impressionTracked, setImpressionTracked] = useState(false);
    const [clickCount, setClickCount] = useState<number>((cta as any).metrics?.clicks || 0);

    // Track Impression via IntersectionObserver
    useEffect(() => {
        if (!cta._id || impressionTracked) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Track impression
                    fetch('/api/ads/public/track/impression', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ adId: cta._id })
                    }).catch(err => console.error('Impression tracking failed:', err));

                    // GA event
                    if (window.gtag) {
                        window.gtag('event', 'ad_impression', {
                            ad_id: cta._id,
                            ad_name: cta.name,
                            ad_type: cta.type,
                            ...(cta.gtagParams || {})
                        });
                    }

                    setImpressionTracked(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.5 }
        );

        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [cta._id, impressionTracked]);

    const handleClick = () => {
        // Track click to backend
        if (cta._id) {
            fetch('/api/ads/public/track/click', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adId: cta._id })
            })
                .then(res => res.json())
                .then(data => {
                    if (data.success) setClickCount(data.metrics.clicks);
                })
                .catch(err => console.error('Click tracking failed:', err));
        }

        // GA event
        if (window.gtag) {
            window.gtag('event', 'ad_click', {
                ad_id: cta._id,
                ad_name: cta.name,
                ad_type: cta.type,
                item_id: cta.hotelId || cta.destinationId,
                item_name: cta.hotelName || cta.headline,
                value: cta.displayPrice || 0,
                currency: cta.currency || 'SEK',
                ...(cta.gtagParams || {})
            });
        }

        if (cta.trackedUrl) {
            window.location.href = cta.trackedUrl;
        } else if (cta.destinationUrl) {
            window.location.href = cta.destinationUrl;
        }
    };

    // Click counter badge
    const ClickBadge = () => clickCount > 0 ? (
        <div className="flex items-center gap-1 px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-[10px] font-bold text-white/80">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            {clickCount.toLocaleString()}
        </div>
    ) : null;

    if (variant === 'slim') {
        return (
            <div
                ref={ref}
                className={`relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-800 text-white shadow-xl cursor-pointer group ${className}`}
                onClick={handleClick}
            >
                {cta.mediaUrl && (
                    <div className="absolute inset-0 opacity-40 group-hover:opacity-50 transition-opacity">
                        <img src={cta.mediaUrl} alt="" className="w-full h-full object-cover" />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent"></div>

                <div className="relative px-6 py-8 md:px-10 md:py-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-xs font-bold uppercase tracking-wider mb-4 border border-yellow-400/30">
                            <span className="material-icons-round text-sm">bolt</span>
                            Flash Sale
                            <ClickBadge />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-black mb-2 drop-shadow-lg">{headline}</h2>
                        <p className="text-white/80 text-sm md:text-base font-medium max-w-xl">{description}</p>
                    </div>

                    <div className="flex items-center gap-6">
                        {cta.displayPrice && (
                            <div className="text-right">
                                <div className="text-xs text-white/60 uppercase font-bold tracking-widest mb-1">{language === 'sv' ? 'Från' : 'Starting from'}</div>
                                <div className="text-3xl md:text-4xl font-black text-yellow-400 drop-shadow-sm">
                                    {cta.displayPrice} {cta.currency || 'SEK'}
                                </div>
                            </div>
                        )}
                        <button className="px-8 py-4 bg-white text-blue-900 rounded-xl font-black text-sm uppercase tracking-widest shadow-lg group-hover:scale-105 transition-all active:scale-95">
                            {ctaText || (language === 'sv' ? 'BOKA NU' : 'BOOK NOW')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (variant === 'hero') {
        return (
            <div
                ref={ref}
                className={`relative rounded-3xl overflow-hidden shadow-2xl group cursor-pointer ${className}`}
                style={{ height: '400px' }}
                onClick={handleClick}
            >
                <img
                    src={cta.mediaUrl || 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?q=80&w=2000'}
                    alt={headline || ''}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

                <div className="absolute top-4 right-4 z-10"><ClickBadge /></div>

                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                    <div className="max-w-3xl">
                        <h2 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight drop-shadow-xl">
                            {headline}
                        </h2>
                        <p className="text-white/80 text-base md:text-lg font-medium mb-8 max-w-2xl">
                            {description}
                        </p>

                        <div className="flex items-center gap-4">
                            <button className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-sm uppercase tracking-widest shadow-xl transition-all hover:-translate-y-1">
                                {ctaText || (language === 'sv' ? 'UPPTÄCK MER' : 'DISCOVER MORE')}
                            </button>
                            {cta.displayPrice && (
                                <div className="px-6 py-4 glass-morphism rounded-xl">
                                    <span className="text-white/60 text-xs font-bold uppercase tracking-widest block mb-0.5">
                                        {language === 'sv' ? 'Pris från' : 'Price from'}
                                    </span>
                                    <span className="text-2xl font-black text-white">
                                        {cta.displayPrice} {cta.currency || 'SEK'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Default In-Feed
    return (
        <div
            ref={ref}
            className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-white/10 overflow-hidden group cursor-pointer flex flex-col md:flex-row hover:border-blue-500/50 transition-colors ${className}`}
            onClick={handleClick}
        >
            <div className="md:w-1/3 relative overflow-hidden">
                <img
                    src={cta.mediaUrl || cta.thumbnailUrl}
                    alt={headline || ''}
                    className="w-full h-64 md:h-full object-cover transition-transform group-hover:scale-110"
                />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="px-3 py-1 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-lg">
                        Promotion
                    </span>
                    <ClickBadge />
                </div>
            </div>
            <div className="p-6 md:p-8 md:w-2/3 flex flex-col justify-center">
                <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 transition-colors">
                    {headline}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm md:text-base mb-6 leading-relaxed">
                    {description}
                </p>
                <div className="flex items-center justify-between mt-auto">
                    {cta.displayPrice ? (
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-0.5">
                                {language === 'sv' ? 'Från' : 'Starting from'}
                            </span>
                            <span className="text-xl font-black text-blue-600">
                                {cta.displayPrice} {cta.currency || 'SEK'}
                            </span>
                        </div>
                    ) : <div />}
                    <button className="flex items-center gap-2 text-sm font-black text-blue-600 uppercase tracking-widest group-hover:gap-3 transition-all">
                        {ctaText || (language === 'sv' ? 'Kolla läget' : 'Check it out')}
                        <span className="material-icons-round text-sm">arrow_forward</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PageCTA;
